// @ts-nocheck
// Store
import {
  FacebookLoginProvider,
  SocialAuthServiceConfig,
} from '@abacritt/angularx-social-login'
import { APP_BASE_HREF } from '@angular/common'
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http'
import {
  ErrorHandler,
  NgModule,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { MatNativeDateModule } from '@angular/material/core'
import { MatIconModule } from '@angular/material/icon'
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button'
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu'
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { RouteReuseStrategy, Router } from '@angular/router'
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive' // this includes the core NgIdleModule but includes keepalive providers for easy wireup
import { EffectsModule } from '@ngrx/effects'
import { MetaReducer, StoreModule } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { provideTranslateService } from '@ngx-translate/core'
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader'
import * as Sentry from '@sentry/angular'
import { LinkyModule } from 'ngx-linky'
import { MomentModule } from 'ngx-moment' // optional, provides moment-style pipes for date formatting
import { BaseModule } from 'shared/base/base.module'
import { SharedComponentModule } from 'shared/shared-components.module'
// App & Modules
import { environment } from '../environments/environment'
import { initializeUserGuiding } from './app-initializers'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { AuthModule } from './auth/auth.module'
import { CustomRouteReuseStrategy } from './customRouteReuseStrategy'
// Effects
import { CopilotConversationEffects } from './analytics/copilot/effects/copilot-conversation.effects'
import { CaseEffects } from './desk/services/case.effects'
import { ProviderIntegrationModule } from './provider-integration/provider-integration.module'
import { localStorageSyncReducer, reducers } from './reducers/reducers'
// Services
import { ColorSchemeService } from './services/color-scheme.service'
import { AuthEffects } from './services/effects/auth.effects'
import { OrganizationEffects } from './services/effects/organization.effects'
import { PusherEffects } from './services/effects/pusher.effects'
import { QueueEffects } from './services/effects/queue.effects'
import { TinybirdApiEffects } from './services/effects/tinybird-api.effects'
import { UserEffects } from './services/effects/user.effects'
import { FeedbackService } from './services/feedback.service'
import { HubtypeAuthInterceptor } from './services/hubtype-api/hubtype-auth.interceptor'
import {
  AdminRoleGuard,
  ManagerOrAdminRoleGuard,
} from './services/role-guard.service'
import { LilaraSidebarWrapperComponent } from './shared/lilara-sidebar/lilara-sidebar-wrapper.component'
import { SharedBrandModule } from './shared/shared-brand/shared-brand.module'
import { WhatsappCallsComponent } from './shared/whatsapp-calls/whatsapp-calls.component'
import { SplashComponent } from './splash/splash.component'

const authConfig = [
  {
    id: FacebookLoginProvider.PROVIDER_ID,
    provider: new FacebookLoginProvider(environment.facebook_app_id, {
      version: 'v23.0',
    }),
  },
]

const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer]

@NgModule({
  declarations: [AppComponent, SplashComponent],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    FlexLayoutModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    AuthModule,
    ProviderIntegrationModule,
    LinkyModule,
    MatNativeDateModule,
    MomentModule,
    NgIdleKeepaliveModule.forRoot(),
    SharedBrandModule,
    SharedComponentModule,
    WhatsappCallsComponent,
    LilaraSidebarWrapperComponent,
    EffectsModule.forRoot([
      CaseEffects, // TODO: Move to forFeature eventually
      PusherEffects,
      QueueEffects,
      UserEffects,
      AuthEffects,
      OrganizationEffects,
      TinybirdApiEffects,
      CopilotConversationEffects,
    ]),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: !isDevMode(),
      connectInZone: true,
    }),
    BaseModule,
  ],
  providers: [
    ColorSchemeService,
    AdminRoleGuard,
    ManagerOrAdminRoleGuard,
    FeedbackService,
    { provide: APP_BASE_HREF, useValue: '/' },
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: authConfig,
      } as SocialAuthServiceConfig,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HubtypeAuthInterceptor,
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    provideAppInitializer(() => {
      const initializerFn = (() => initializeUserGuiding)()
      return initializerFn()
    }),
    ...(environment.sentryURL
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
              showDialog: false,
            }),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          provideAppInitializer(() => {
            const initializerFn = (() => () => {})(inject(Sentry.TraceService))
            return initializerFn()
          }),
        ]
      : []),
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: 'assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'en',
      lang: 'en',
    }),
  ],
})
export class AppModule {
  constructor() {}
}
